import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import yaml from "yaml";
import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";

import { ApiError } from "@api";
import {
  DEFAULT_LANG,
  DEFAULT_RULE,
  Feature,
  ID_BY_KEY,
  Language,
} from "@utils";

import { run as postRun } from "@pages/editor/api";
import {
  RuleValidationError,
  RunResponse,
  SimpleRule,
} from "@pages/editor/types";
import {
  ruleFromYAML,
  validateSemgrepYaml,
  yamlFromRule,
} from "@pages/editor/utils";
import {
  postRule,
  postRuleset,
  RulePostParams,
  RulesetPostParams,
} from "@pages/registry/api";
import {
  RegistryRule,
  RegistryRuleMetadata,
  RegistryTaintRule,
  Visibility,
} from "@pages/registry/types";

import { setLocalStorageBundle } from "../utils/localStorageBundle";

import { ErrorResult, SuccessResult } from "./Result";
import { Workbench } from "./Workbench";

type DisplayProps = {
  lastChangeAt?: string;
  lastChangeBy?: string;
  sourceUri?: string;
};

export class Bundle {
  workbench: Workbench;
  ruleText: string;
  targetText: string;
  visibility: Visibility;
  isRunning: boolean = false;
  displayProps: DisplayProps;
  isDeep: boolean = false;

  /**
   * Last title is used to keep track of the last valid rule_id for the UI.
   *
   * Note: This parameter is necessary because `rule` is null anytime the
   * current `ruleText` is unparseable. In that sense, we keep track of the last
   * _valid_ ID so that we can display that as the editor title.
   */
  lastTitle: string = "Unknown Rule";

  constructor(
    workbench: Workbench,
    ruleText: string,
    targetText: string,
    visibility: Visibility,
    displayProps?: DisplayProps
  ) {
    makeAutoObservable(this, { workbench: false }, { autoBind: true });

    /**
     * Anytime the ruletext is updated, update the last valid title for the rule
     * with a debounce due to expensive YAML parsing.
     */
    reaction(() => this.ruleText, debounce(this.updateLastTitle, 100));

    this.workbench = workbench;
    this.ruleText = ruleText;
    this.targetText = targetText;
    this.visibility = visibility;
    this.displayProps = displayProps || {};
    this.updateLastTitle();
  }

  setRuleText(value: string) {
    if (this.workbench.isPlayground) {
      setLocalStorageBundle({
        rule: value,
        target: this.targetText,
      });
    }

    this.ruleText = value;
  }

  updateLastTitle() {
    if (this.rule) {
      this.lastTitle = this.rule.id;
    }
  }

  setTargetText(value: string) {
    if (this.workbench.isPlayground) {
      setLocalStorageBundle({
        rule: this.ruleText,
        target: value,
      });
    }
    this.targetText = value;
  }

  setRuleId(value: string) {
    if (this.rule) {
      let ruleCopy = cloneDeep(this.rule);
      ruleCopy.id = value;
      this.ruleText = yaml.stringify(ruleCopy);
    }
  }

  setRuleMetadata(value: RegistryRuleMetadata) {
    if (this.rule) {
      let ruleCopy = cloneDeep(this.rule);
      ruleCopy.metadata = value;
      this.ruleText = yaml.stringify(ruleCopy);
    }
  }

  setRuleMessage(value: string) {
    if (this.rule) {
      let ruleCopy = cloneDeep(this.rule);
      ruleCopy.message = value;
      this.ruleText = yaml.stringify(ruleCopy);
    }
  }

  setRuleLanguages(value: string[]) {
    if (this.rule) {
      let ruleCopy = cloneDeep(this.rule);
      ruleCopy.languages = value;
      this.ruleText = yaml.stringify(ruleCopy);
    }
  }

  get rule(): RegistryRule | RegistryTaintRule | null {
    try {
      const rule = yaml.parse(this.ruleText);
      return rule.rules[0] as RegistryRule | RegistryTaintRule;
    } catch (e) {
      return null;
    }
  }

  getSimpleRuleInfo():
    | { simpleRule: SimpleRule | undefined; lossless: boolean }
    | undefined {
    if (this.validate().length > 0) {
      return undefined;
    }

    const [simpleRule, lossless] = ruleFromYAML(
      this.ruleText,
      DEFAULT_RULE(this.language ?? DEFAULT_LANG)
    );
    if (simpleRule) {
      return { simpleRule, lossless };
    } else {
      return undefined;
    }
  }

  get simpleRule(): SimpleRule | undefined {
    try {
      return this.getSimpleRuleInfo()?.simpleRule;
    } catch (e) {
      return undefined;
    }
  }

  onSimpleRuleChange(newRule: SimpleRule) {
    runInAction(() => {
      this.ruleText = yamlFromRule(newRule);
      setLocalStorageBundle({
        rule: this.ruleText,
        target: this.targetText,
      });
    });
  }

  get language(): Language | null {
    try {
      if (this.rule === null) return null;
      return this.rule.languages[0];
    } catch (e) {
      return null;
    }
  }

  setLanguage(language: Language) {
    if (this.rule === null) return;
    let ruleCopy = cloneDeep(this.rule);
    ruleCopy.languages = [language];
    this.ruleText = yaml.stringify(ruleCopy);
  }

  clone(): Bundle {
    return new Bundle(
      this.workbench,
      this.ruleText,
      this.targetText,
      this.visibility
    );
  }

  isEqualTo(other: Bundle | null): boolean {
    return (
      this.ruleText === other?.ruleText && this.targetText === other?.targetText
    );
  }

  /**
   * The line numbers tagged by the match string (found in comments preceding the line).
   */
  findTaggedLines(matchRegExp: string): number[] {
    const tuples: [string, number][] = this.targetText
      .split("\n")
      // we use `index + 2` because:
      // a) line numbering starts from 1, not 0
      // b) a match is expected on the following line compared to where the ruleid comment is
      .map((line, index) => [line, index + 2]);

    return tuples
      .filter(([line, _]) => (line as string).match(new RegExp(matchRegExp)))
      .map(([_, lineno]) => lineno);
  }

  /**
   * The line numbers on which the rule should match according to test comments.
   */
  get expectedMatches(): number[] {
    return this.rule ? this.findTaggedLines(`ruleid: ?${this.rule.id}`) : [];
  }

  /**
   * The line numbers on which the rule should not match according to test comments.
   */
  get expectedNotMatches(): number[] {
    return this.rule ? this.findTaggedLines(`ok: ?${this.rule.id}`) : [];
  }

  /**
   * Saves a snippet with visibility "unlisted"
   * Returns validation errors, if found, or snippetId (on success)
   * Throws error on API error.
   */
  async saveSnippet(): Promise<ErrorResult | string> {
    const errors = this.validate();
    let saveMetadata: RulePostParams = {
      definition: { rules: [this.rule] },
      language: this.language!,
      test_target: this.targetText,
      visibility: "unlisted",
    };
    if (errors && errors.length > 0) {
      datadogRum.addAction("Save Snippet", {
        ...saveMetadata,
        rule: this.rule,
        message: "Snippet save failed due to validation errors.",
        errors: errors,
      });

      return new ErrorResult(this, errors);
    }

    const handleSaveAPIError = (err: ApiError) => {
      let message =
        err.statusCode >= 400 && err.statusCode < 500
          ? "Saving failed: validation error (try running the rule)"
          : "Saving failed: critical error (try later)";

      datadogRum.addAction("Save Snippet", {
        ...saveMetadata,
        rule: this.rule,
        errors: [err],
        message: "Snippet save failed due to API errors.",
      });

      throw new Error(message);
    };

    const { id: savedId } = await postRule(saveMetadata).catch(
      handleSaveAPIError
    );

    datadogRum.addAction("Save Snippet", {
      ...saveMetadata,
      rule: this.rule,
      savedId: savedId,
      message: "Snippet save succeeded.",
    });

    return savedId;
  }

  /**
   * Save the rule to the backend, and create a fitting data store locally.
   * If a rule with this id already exists, it will be overwritten.
   * Returns validation errors, if found.
   * Throws error on API error.
   */
  async save(snippetFullName: string): Promise<ErrorResult | undefined> {
    const errors = this.validate();
    let saveMetadata: RulePostParams = {
      definition: { rules: [this.rule] },
      language: this.language!,
      test_target: this.targetText,
      visibility: this.visibility,
    };

    if (this.visibility === "org_private") {
      saveMetadata = { ...saveMetadata, deployment_id: this.workbench.org!.id };
    }

    if (errors && errors.length > 0) {
      datadogRum.addAction("Save Rule", {
        ...saveMetadata,
        rule: this.rule,
        snippetName: snippetFullName,
        message: "Rule save failed due to validation errors.",
        errors: errors,
      });

      return new ErrorResult(this, errors);
    }

    const handleSaveAPIError = (err: ApiError) => {
      const serverMessage = err.body;
      let message =
        "Saving failed: " +
        (typeof serverMessage?.error === "string"
          ? serverMessage?.error
          : err.statusCode >= 400 && err.statusCode < 500
          ? "validation error (try running the rule)"
          : "critical error (try later)");

      datadogRum.addAction("Save Rule", {
        ...saveMetadata,
        rule: this.rule,
        snippetName: snippetFullName,
        errors: [err],
        message: "Rule save failed due to API errors.",
      });

      throw new Error(message);
    };

    // TODO: Fix this disco coding. There are many assumptions here that
    // don't always hold up. Like the snippet formatting/always saving rulesets.
    const { id: savedId } = await postRule(saveMetadata).catch(
      handleSaveAPIError
    );

    let rulesetData: RulesetPostParams = {
      rules: [{ id: savedId }],
    };
    if (saveMetadata.deployment_id) {
      rulesetData = {
        ...rulesetData,
        deployment_id: saveMetadata.deployment_id,
      };
    }

    await postRuleset(snippetFullName, rulesetData).catch(handleSaveAPIError);

    datadogRum.addAction("Save Rule", {
      ...saveMetadata,
      rule: this.rule,
      snippetName: snippetFullName,
      savedId: savedId,
      message: "Rule save succeeded.",
    });

    return undefined;
  }

  validate(): RuleValidationError[] {
    var errors: RuleValidationError[] = [];
    try {
      errors = validateSemgrepYaml(this.ruleText);
    } catch {}
    return errors;
  }

  async run() {
    if (!this.rule) {
      return;
    }
    runInAction(() => {
      this.isRunning = true;
    });

    const { org, history } = this.workbench;
    const urlParams = new URLSearchParams(history.location.search);
    const isDevelop = urlParams.get("develop") === "true";
    // org is undefined if user is logged out
    const snippetFullName = org ? `${org.name}:${this.rule.id}` : this.rule.id;
    /**
     * For enforced security, will only reference `this.isDeep` if hasDeeperRun
     * is enabled, otherwise will always pass False.
     */
    const withDeep = Feature.hasDeeperRun({ org }) ? this.isDeep : false;

    const actionMetadata = {
      snippetName: snippetFullName,
      definition: { rules: [this.rule] },
      rule: this.rule,
      language: this.language!,
      test_target: this.targetText,
      visibility: this.visibility,
      deep: withDeep,
    };

    const errors = this.validate();
    if (errors && errors.length > 0) {
      runInAction(() => {
        this.isRunning = false;
        this.workbench.resultsHistory.push(new ErrorResult(this, errors));
      });

      datadogRum.addAction("Run Rule", {
        ...actionMetadata,
        message: "Rule run failed due to validation errors.",
        errors: errors,
      });

      return;
    }

    let response: RunResponse;

    try {
      response = await postRun(
        {
          pattern: yaml.stringify(yaml.parse(this.ruleText)),
          // language will not be null because validate() has been run
          language: ID_BY_KEY[this.language!.toLowerCase()],
          target: this.targetText,
        },
        true,
        { is_develop: isDevelop, is_deep: withDeep }
      );
    } catch (err) {
      if (err instanceof ApiError) {
        runInAction(() => {
          this.isRunning = false;
          this.workbench.resultsHistory.push(
            ErrorResult.fromApiResult(this, err as ApiError)
          );
        });

        datadogRum.addAction("Run Rule", {
          ...actionMetadata,
          message: "Rule run failed due to API errors.",
          errors: [err],
        });
      } else {
        Sentry.captureException(err);

        datadogRum.addAction("Run Rule", {
          ...actionMetadata,
          message: "Rule run failed due to unknown errors.",
          errors: [err],
        });
      }
      return;
    }

    const successResult = new SuccessResult(this, response);
    if (successResult.errors.length > 0) {
      datadogRum.addAction("Run Rule", {
        ...actionMetadata,
        message: "Rule run failed due to runtime errors.",
        errors: response.semgrep_result.output.errors,
      });
    } else {
      datadogRum.addAction("Run Rule", {
        ...actionMetadata,
        message: "Rule run succeeded.",
      });
    }

    runInAction(() => {
      this.isRunning = false;
      this.workbench.resultsHistory.push(successResult);
    });
  }

  toggleDeep() {
    this.isDeep = !this.isDeep;
  }
}

export type ReadonlyBundle = Bundle & {
  ruleText: Readonly<string>;
  targetText: Readonly<string>;
};
